import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [paginationContainer] - The pagination controls container.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadingSpinner] - The loading spinner element.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /**
   * Cached HTML for all visible cards across all backend pages for the
   * current filter state. Used for client-side pagination to guarantee a
   * consistent number of items per page even when some products are hidden
   * by Liquid.
   * @type {string[]}
   */
  allCardsHTML = [];

  /**
   * Promise used to avoid rebuilding the cached cards multiple times.
   * @type {Promise<void> | null}
   */
  allCardsPromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  aspectRatioHelper;

  /** @type {number} */
  currentPage = 1;

  /** @type {boolean} */
  hasFilterUpdateListener = false;

  /** @type {((event: Event) => void) | null} */
  boundHandleFilterUpdate = null;

  /** @type {((event: Event) => void) | null} */
  boundPaginationClickHandler = null;

  /** @type {boolean} */
  hasPaginationListener = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    const urlParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(urlParams.get('page') || '1', 10);

    this.setProductCountVisibility(false);
    this.setLoadingState(true);

    requestAnimationFrame(() => {
      // Sync sort_by URL parameter on load if not present
      // Do this in requestAnimationFrame to ensure DOM is ready
      this.#syncSortByURL();

      this.renderPaginationControls();
      this.updateProductCount();

      this.setupPaginationListeners();

      this.updatePageTitle(this.currentPage);

      requestIdleCallback(() => {
        this.buildAllCardsIfNeeded().then(() => {
          if (this.isConnected) {
            this.renderClientPage(this.currentPage);
            this.updateProductCount();
            this.setProductCountVisibility(true);
            this.updatePageTitle(this.currentPage);
          }
        });
      });
    });

    if (!this.hasFilterUpdateListener) {
      if (!this.boundHandleFilterUpdate) {
        this.boundHandleFilterUpdate = this.handleFilterUpdate.bind(this);
      }
      const handler = this.boundHandleFilterUpdate;
      if (handler) {
        document.addEventListener(ThemeEvents.FilterUpdate, handler);
        this.hasFilterUpdateListener = true;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.hasFilterUpdateListener && this.boundHandleFilterUpdate) {
      document.removeEventListener(ThemeEvents.FilterUpdate, this.boundHandleFilterUpdate);
      this.hasFilterUpdateListener = false;
    }
  }

  setupPaginationListeners() {
    const { paginationContainer } = this.refs;
    if (!paginationContainer) return;

    // Remove existing listener if it exists
    if (this.hasPaginationListener && this.boundPaginationClickHandler) {
      paginationContainer.removeEventListener('click', this.boundPaginationClickHandler);
      this.hasPaginationListener = false;
    }

    // Create and bind the handler
    if (!this.boundPaginationClickHandler) {
      this.boundPaginationClickHandler = async (e) => {
        const target = /** @type {Element} */ (e.target);
        if (!target) return;

        const button = target.closest('.pagination__button');
        if (button && button instanceof HTMLElement && button.dataset.page) {
          e.preventDefault();
          const page = parseInt(button.dataset.page, 10);
          if (!isNaN(page)) {
            await this.goToPage(page);
          }
        }
      };
    }

    // Add the listener
    paginationContainer.addEventListener('click', this.boundPaginationClickHandler);
    this.hasPaginationListener = true;
  }

  /**
   * Renders the pagination controls.
   *
   * @param {number} [totalPagesOverride] - Optional override for total pages
   * used by client-side pagination. When not provided, falls back to the
   * value from the grid dataset (backend pagination).
   */
  renderPaginationControls(totalPagesOverride) {
    const { grid, paginationContainer } = this.refs;
    if (!grid) return;

    // Create pagination container if it doesn't exist
    let container = paginationContainer;
    if (!container) {
      container = document.createElement('div');
      container.className = 'pagination-wrapper';
      container.setAttribute('ref', 'paginationContainer');
      if (grid.parentElement) {
        grid.parentElement.appendChild(container);
      } else {
        grid.insertAdjacentElement('afterend', container);
      }
      // Update refs
      this.refs.paginationContainer = container;
    }

    const totalPages =
      typeof totalPagesOverride === 'number' ? totalPagesOverride : parseInt(grid.dataset.lastPage || '1', 10);
    let currentPage = this.currentPage;

    // Validate current page is within bounds
    if (currentPage < 1 || currentPage > totalPages) {
      currentPage = 1;
      this.currentPage = 1;
    }

    // Only render pagination if there's more than 1 page
    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '<div class="pagination">';

    // Previous button
    if (currentPage > 1) {
      html += `<button class="pagination__button pagination__prev" data-page="${currentPage - 1}" aria-label="Previous page">
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M15.8327 10.0001H4.16602M4.16602 10.0001L9.99935 15.8334M4.16602 10.0001L9.99935 4.16675" stroke="#909090" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
      <span>Previous</span>
      </button>`;
    }

    // Wrap page numbers in a div
    html += '<div class="pagination__numbers">';

    // Page numbers
    const pagesToShow = this.getPageNumbers(currentPage, totalPages);
    for (const page of pagesToShow) {
      if (page === '...') {
        html += '<span class="pagination__ellipsis">...</span>';
      } else {
        const isActive = page === currentPage ? 'pagination__button--active' : '';
        html += `<button class="pagination__button ${isActive}" data-page="${page}" aria-label="Page ${page}" ${page === currentPage ? 'aria-current="page"' : ''}>${page}</button>`;
      }
    }

    html += '</div>'; // Close pagination__numbers

    // Next button
    if (currentPage < totalPages) {
      html += `<button class="pagination__button pagination__next" data-page="${currentPage + 1}" aria-label="Next page">
      <span>Next</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4.16602 10.0001H15.8327M15.8327 10.0001L9.99935 4.16675M15.8327 10.0001L9.99935 15.8334" stroke="#909090" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
      </svg> 
      </button>`;
    }

    html += '</div>';
    container.innerHTML = html;

    // Ensure refs are updated and event listeners are set up
    this.refs.paginationContainer = container;
    // Use requestAnimationFrame to ensure DOM is updated before setting up listeners
    requestAnimationFrame(() => {
      this.setupPaginationListeners();
    });
  }

  /**
   * @param {number} current - Current page number
   * @param {number} total - Total number of pages
   * @returns {Array<number | string>} Array of page numbers and ellipsis
   */
  getPageNumbers(current, total) {
    const pages = [];
    const maxVisible = 7;

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      } else if (current >= total - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      }
    }

    return pages;
  }

  updateProductCount() {
    const countElements = document.querySelectorAll('.products-count');
    if (countElements.length === 0) return;
    const { grid } = this.refs;
    if (!grid) return;

    const cardsOnPage = grid.querySelectorAll('[ref="cards[]"]').length;
    const itemsOnPage = cardsOnPage;

    const visibleTotal =
      Array.isArray(this.allCardsHTML) && this.allCardsHTML.length > 0
        ? this.allCardsHTML.length
        : parseInt(grid.dataset.totalItems || '0', 10);

    const newText = `Showing ${itemsOnPage} of ${visibleTotal} items`;

    // Update all product count elements (both desktop and drawer)
    countElements.forEach((element) => {
      element.innerHTML = newText;
    });
  }

  /**
   * Updates the page title with the current page number
   * @param {number} page - The current page number
   */
  updatePageTitle(page) {
    if (!page || page < 1) return;

    const currentTitle = document.title;

    // Remove existing "Page X" from title if present
    const titleWithoutPage = currentTitle.replace(/\s*–\s*Page\s+\d+/gi, '').trim();

    // Extract shop name if it exists at the end (format: "Title – Shop Name")
    // Try to find shop name pattern at the end
    const shopNameMatch = titleWithoutPage.match(/^(.+?)\s*–\s*([^–]+)$/);

    if (shopNameMatch && shopNameMatch[1] && shopNameMatch[2]) {
      // Title has shop name at the end: "All products – GEN-Y HITCH"
      const baseTitle = shopNameMatch[1].trim();
      const shopName = shopNameMatch[2].trim();
      // Insert page number before shop name: "All products – Page 3 – GEN-Y HITCH"
      document.title = `${baseTitle} – Page ${page} – ${shopName}`;
    } else {
      // Title doesn't have shop name, just add page number
      document.title = `${titleWithoutPage} – Page ${page}`;
    }
  }

  /**
   * @param {number} page - The page number to navigate to
   */
  async goToPage(page) {
    const { grid } = this.refs;
    if (!grid) return;

    // Ensure we have a complete list of visible cards for the current
    // filter state so we can paginate client-side.
    await this.buildAllCardsIfNeeded();

    const pageSize = parseInt(grid.dataset.pageSize || '16', 10);
    const totalPages = this.allCardsHTML.length > 0 ? Math.max(1, Math.ceil(this.allCardsHTML.length / pageSize)) : 1;

    if (page < 1 || page > totalPages) return;

    // Show loading state
    grid.style.opacity = '0.5';
    grid.style.pointerEvents = 'none';

    try {
      // Render the requested page using client-side pagination.
      this.renderClientPage(page);

      // Update product count display
      this.updateProductCount();
      this.setProductCountVisibility(true);

      // Update page title
      this.updatePageTitle(page);

      // Update URL (remove section_id parameter)
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.set('page', page.toString());
      cleanUrl.searchParams.delete('section_id');
      cleanUrl.hash = '';
      history.pushState('', '', cleanUrl.toString());

      // Scroll to top of grid
      const gridTop = grid.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(0, gridTop - 100),
        behavior: 'smooth',
      });

      // Process aspect ratios if needed
      if (this.aspectRatioHelper) {
        this.aspectRatioHelper.processNewElements();
      }
    } finally {
      // Remove loading state
      grid.style.opacity = '';
      grid.style.pointerEvents = '';
    }
  }

  /**
   * Builds the cached list of all visible card HTML strings for the current
   * filter state by fetching all backend pages once. Subsequent calls reuse
   * the same promise.
   *
   * @returns {Promise<void>}
   */
  async buildAllCardsIfNeeded() {
    if (this.allCardsPromise) {
      return this.allCardsPromise;
    }

    this.allCardsPromise = this.#buildAllCards();
    return this.allCardsPromise;
  }

  /**
   * Internal implementation that fetches all backend pages and builds the
   * full list of visible cards.
   *
   * @returns {Promise<void>}
   */
  async #buildAllCards() {
    const { grid } = this.refs;
    if (!grid) return;

    const totalBackendPages = parseInt(grid.dataset.lastPage || '1', 10);
    const currentUrl = new URL(window.location.href);
    const currentPage = parseInt(currentUrl.searchParams.get('page') || '1', 10);
    const sortBy = currentUrl.searchParams.get('sort_by');

    // Start with empty array - we'll fetch ALL pages to ensure consistency
    this.allCardsHTML = [];

    const baseUrl = new URL(window.location.href);
    baseUrl.hash = '';
    baseUrl.searchParams.delete('page');

    const fetchPromises = [];
    /** @type {Map<number, string[]>} */
    const pageResultsMap = new Map();
    /** @type {{ page: number; itemCount: number; url: string }[]} */
    const pageResults = [];

    // Fetch ALL pages (1 through totalBackendPages) to ensure complete cache
    for (let backendPage = 1; backendPage <= totalBackendPages; backendPage += 1) {
      const url = new URL(baseUrl.toString());
      if (backendPage > 1) {
        url.searchParams.set('page', backendPage.toString());
      }

      const fetchPromise = sectionRenderer
        .getSectionHTML(this.sectionId, true, url)
        .then((pageContent) => {
          if (!pageContent) {
            return { page: backendPage, items: [] };
          }

          const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
          const newGridElement = parsedPage.querySelector('[ref="grid"]');
          if (!newGridElement) {
            return { page: backendPage, items: [] };
          }

          const newItems = newGridElement.querySelectorAll(':scope > [ref="cards[]"]');
          const itemsArray = Array.from(newItems, (element) => element.outerHTML);
          const itemCount = itemsArray.length;

          return { page: backendPage, items: itemsArray, itemCount, url: url.href };
        })
        .catch(() => {
          return { page: backendPage, items: [] };
        });

      fetchPromises.push(fetchPromise);
    }

    if (fetchPromises.length > 0) {
      const results = await Promise.all(fetchPromises);

      // Store results in map by page number to preserve order
      for (const result of results) {
        if (result && result.items) {
          pageResultsMap.set(result.page, result.items);
          if ('itemCount' in result && result.itemCount !== undefined && 'url' in result && result.url) {
            pageResults.push({ page: result.page, itemCount: result.itemCount, url: result.url });
          }
        }
      }

      // Concatenate pages in order (1, 2, 3...) regardless of fetch completion order
      for (let pageNum = 1; pageNum <= totalBackendPages; pageNum += 1) {
        const pageItems = pageResultsMap.get(pageNum);
        if (pageItems && pageItems.length > 0) {
          this.allCardsHTML.push(...pageItems);
        }
      }
    }
  }

  /**
   * Renders a specific client-side page from the cached list of cards.
   *
   * @param {number} page - 1-based page index
   */
  renderClientPage(page) {
    const { grid } = this.refs;
    if (!grid) return;

    // If we have client-side cards, use them for pagination
    if (this.allCardsHTML.length > 0) {
      const pageSize = parseInt(grid.dataset.pageSize || '16', 10);
      const totalPages = Math.max(1, Math.ceil(this.allCardsHTML.length / pageSize));

      let targetPage = page;
      if (targetPage < 1 || targetPage > totalPages) {
        targetPage = 1;
      }

      this.currentPage = targetPage;

      const startIndex = (targetPage - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, this.allCardsHTML.length);

      const cardsToRender = this.allCardsHTML.slice(startIndex, endIndex);
      const pageHTML = cardsToRender.join('');

      grid.innerHTML = pageHTML;

      // Re-render pagination controls using the client-side total.
      this.renderPaginationControls(totalPages);

      // Update page title
      this.updatePageTitle(targetPage);
    } else {
      // If no client-side cards yet, use backend pagination value
      const totalPages = parseInt(grid.dataset.lastPage || '1', 10);
      this.renderPaginationControls(totalPages);
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by re-rendering pagination controls.
   * Implemented as a regular method so subclasses can override and call super.
   */
  handleFilterUpdate() {
    this.pages.clear();

    this.allCardsHTML = [];
    this.allCardsPromise = null;

    this.currentPage = 1;

    const { grid, paginationContainer } = this.refs;
    if (!grid) {
      return;
    }

    let savedPaginationHTML = '';

    if (paginationContainer) {
      savedPaginationHTML = paginationContainer.innerHTML;
      paginationContainer.classList.add('pagination-loading');
    }

    const currentLastPage = grid.dataset.lastPage;

    this.setProductCountVisibility(false);
    this.setLoadingState(true);

    /**
     * @param {string} hookLabel
     */
    const renderAfterFilters = (hookLabel) => {
      if (!this.isConnected) {
        return;
      }

      // Get fresh reference to pagination container (it might have been recreated)
      const currentPaginationContainer = this.refs.paginationContainer;
      if (currentPaginationContainer) {
        currentPaginationContainer.classList.remove('pagination-loading');
      }

      // Don't render pagination here - wait until after cards are built
      // so we can use the correct filtered count
      this.updateProductCount();

      requestIdleCallback(() => {
        this.buildAllCardsIfNeeded().then(() => {
          if (!this.isConnected) return;
          this.renderClientPage(this.currentPage);
          this.updateProductCount();
          this.setProductCountVisibility(true);
          this.setLoadingState(false);
          this.updatePageTitle(this.currentPage);
        });
      });
    };

    const observer = new MutationObserver(() => {
      const { grid: currentGrid } = this.refs;
      // Get fresh reference to pagination container
      const currentContainer = this.refs.paginationContainer;

      if (currentContainer && currentContainer.innerHTML.trim() === '' && savedPaginationHTML) {
        currentContainer.innerHTML = savedPaginationHTML;
      }

      const newLastPage = currentGrid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();
        renderAfterFilters('handleFilterUpdate:post-build');
      }
    });

    observer.observe(grid, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true,
    });

    if (paginationContainer) {
      observer.observe(paginationContainer, {
        childList: true,
        subtree: true,
      });
    }

    setTimeout(() => {
      observer.disconnect();
      renderAfterFilters('handleFilterUpdate:timeout-post-build');
    }, 400);
  }

  /**
   * @param {boolean} isVisible
   */
  setProductCountVisibility(isVisible) {
    const wrappers = document.querySelectorAll('.products-count-wrapper');
    wrappers.forEach((wrapper) => {
      if (wrapper instanceof HTMLElement) {
        wrapper.classList.toggle('products-count-wrapper--visible', isVisible);
      }
    });

    if (isVisible) {
      this.setLoadingState(false);
    }
  }

  /**
   * @param {boolean} isLoading
   */
  setLoadingState(isLoading) {
    if (!this.hasAttribute('data-spark-collection')) {
      return;
    }

    const { grid, loadingSpinner } = this.refs;
    if (grid instanceof HTMLElement) {
      grid.classList.toggle('sparklayer-product-list--loading', isLoading);
      if (!isLoading) {
        grid.classList.add('sparklayer-product-list--ready');
      } else {
        grid.classList.remove('sparklayer-product-list--ready');
      }
    }
    if (loadingSpinner instanceof HTMLElement) {
      if (isLoading) {
        loadingSpinner.classList.remove('results-loading-spinner--hidden');
        loadingSpinner.setAttribute('aria-hidden', 'false');
      } else {
        loadingSpinner.classList.add('results-loading-spinner--hidden');
        loadingSpinner.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Syncs the sort_by URL parameter on load if it's not present but a sort_by
   * value is selected in the form. This ensures the URL reflects the current
   * sort state, even when using the default sort.
   */
  #syncSortByURL() {
    const urlParams = new URLSearchParams(window.location.search);

    // If sort_by is already in the URL, no need to sync
    if (urlParams.has('sort_by')) {
      return;
    }

    // Find the sort_by form element (select or checked radio)
    // Search within the section that contains this component first, then fall back to document
    const section = this.closest('.shopify-section');

    let selectElement = section ? section.querySelector('select[name="sort_by"]') : null;
    if (!selectElement) {
      selectElement = document.querySelector('select[name="sort_by"]');
    }

    let sortByValue = null;

    if (selectElement instanceof HTMLSelectElement) {
      sortByValue = selectElement.value;
    } else {
      // Try to find a checked radio button
      let checkedRadio = section ? section.querySelector('input[type="radio"][name="sort_by"]:checked') : null;
      if (!checkedRadio) {
        checkedRadio = document.querySelector('input[type="radio"][name="sort_by"]:checked');
      }
      if (checkedRadio instanceof HTMLInputElement) {
        sortByValue = checkedRadio.value;
      }
    }

    // If we found a sort_by value, update the URL to include it
    if (sortByValue) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortByValue);
      // Use replaceState to avoid adding to history
      history.replaceState({}, '', url.toString());
    }
  }
}
